import { IFramework, IWebServiceManager, HttpMethod, IUserContext } from "@truckdown/framework";
import { ILocationActionsService } from "./ILocationActionsService";
import { AddLocationAnalyticModel } from "./Models/AddLocationAnalyticModel";
import { ThrottleResponse } from "./Models/ThrottleResponse";
import { ListingPhoneNumber } from "./Models/ListingPhoneNumber";
import { Listing } from "./Models/Listing";
import { ApiResultModel } from "./Models/ApiResultModel";

class LocationActionsService implements ILocationActionsService {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;
    private readonly basePath: string = '/locations';

    private createResponse<T>(result: T): ThrottleResponse<T> {
        return {
            blocked: false,
            captcha: false,
            warning: '',
            result: result
        };
    }

    verify(locationId: string): Promise<boolean> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/verify/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return true;
                }

                if (res.status == 404) {
                    return false;
                }

                console.log("Error verifying location " + locationId, res);
                return false;
            });
    }

    private throttleWarningHeader = 'x-throttle-warn';

    addCall(locationId: string): Promise<ThrottleResponse<ListingPhoneNumber[]>> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/add-call/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((json) => {
                            var j = json as ThrottleResponse<ListingPhoneNumber[]>;
                            j.warning = res.headers.get(this.throttleWarningHeader) ?? '';
                            return j;
                        });
                }


                if (res.status == 429) {
                    return {
                        blocked: false,
                        captcha: true,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: [] as ListingPhoneNumber[]
                    } as ThrottleResponse<ListingPhoneNumber[]>;
                }

                if (res.status == 403) {
                    return {
                        blocked: true,
                        captcha: false,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: [] as ListingPhoneNumber[]
                    } as ThrottleResponse<ListingPhoneNumber[]>;
                }

                console.log("Error getting phone numbers for " + locationId, res);
                return this.createResponse<ListingPhoneNumber[]>([] as ListingPhoneNumber[]);
            });
    }

    viewDetail(locationId: string): Promise<ThrottleResponse<Listing | null>> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/view-detail/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((json) => {
                            var j = json as ThrottleResponse<Listing | null>;
                            j.warning = res.headers.get(this.throttleWarningHeader) ?? '';
                            return j;
                        });
                }


                if (res.status == 429) {
                    return {
                        blocked: false,
                        captcha: true,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: null
                    } as ThrottleResponse<Listing | null>;
                }

                if (res.status == 403) {
                    return {
                        blocked: true,
                        captcha: false,
                        warning: res.headers.get(this.throttleWarningHeader) ?? '',
                        result: null
                    } as ThrottleResponse<Listing | null>;
                }

                console.log("Error getting details for " + locationId, res);
                return this.createResponse<Listing | null>(null);
            });
    }

    viewWebsite(locationId: string): Promise<void> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/view-website/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return;
                }

                console.log("Error adding view website anaytics", res);
                return;
            });
    }

    showOnMap(locationId: string): Promise<void> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/show-on-map/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return;
                }

                console.log("Error adding show on map anaytics", res);
                return;
            });
    }

    showListing(locationId: string): Promise<void> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/show-listing/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return;
                }

                console.log("Error adding show listing anaytics", res);
                return;
            });
    }

    addLocationAnalytic(model: AddLocationAnalyticModel): Promise<void> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/add-location-analytic',
            method: HttpMethod.PUT
        }, model);

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return;
                }

                console.log("Error adding location anaytics", res);
                return;
            });
    }


    submitRemoveLocationRequest(locationId: string): Promise<ApiResultModel> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/remove-listing/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager.get<ApiResultModel>(req);
    }

    submitUpdradeLocationRequest(locationId: string): Promise<ApiResultModel> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/upgrade-listing/' + encodeURIComponent(locationId),
            method: HttpMethod.PUT
        });

        return this.manager.get<ApiResultModel>(req);
    }
}

export default (framework: IFramework) => {
    framework.registerTransient<ILocationActionsService>('ILocationActionsService', (frm) => {
        return new LocationActionsService(frm);
    });
}
